import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Avatar, AvatarFallback } from '../components/ui/avatar';
import { Baby, Plus, Ruler, LogOut, User, TrendingUp } from 'lucide-react';
import { calculateAge } from '../mock';
import Logo from '../components/Logo';

const Dashboard = () => {
  const navigate = useNavigate();
  const { user, children, logout, getChildMeasurements } = useAuth();

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const getLatestMeasurement = (childId) => {
    const measurements = getChildMeasurements(childId);
    return measurements[0] || null;
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#FFF5F0] via-[#E5F5FF] to-[#F0FFE5]">
      {/* Header */}
      <nav className="border-b bg-white/80 backdrop-blur-sm sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex justify-between items-center">
          <div className="cursor-pointer" onClick={() => navigate('/dashboard')}>
            <Logo />
          </div>
          <div className="flex gap-3 items-center">
            <Button variant="ghost" onClick={() => navigate('/size-guide')}>
              Size Guide
            </Button>
            <div className="flex items-center gap-2 text-sm text-gray-600">
              <User className="w-4 h-4" />
              {user?.name}
            </div>
            <Button variant="outline" size="sm" onClick={handleLogout}>
              <LogOut className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </nav>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Welcome Section */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-800 mb-2">Welcome back, {user?.name}!</h1>
          <p className="text-gray-600">Manage your children's foot measurements and track their growth.</p>
        </div>

        {/* Quick Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <Card className="p-6 bg-gradient-to-br from-pink-400 to-pink-500 text-white">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-pink-100 text-sm">Total Children</p>
                <p className="text-3xl font-bold mt-1">{children.length}</p>
              </div>
              <Baby className="w-12 h-12 opacity-80" />
            </div>
          </Card>
          <Card className="p-6 bg-gradient-to-br from-[#A8D5FF] to-[#9ADFB4] text-white">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-purple-100 text-sm">Total Measurements</p>
                <p className="text-3xl font-bold mt-1">{children.reduce((sum, child) => sum + getChildMeasurements(child.id).length, 0)}</p>
              </div>
              <Ruler className="w-12 h-12 opacity-80" />
            </div>
          </Card>
          <Card className="p-6 bg-gradient-to-br from-blue-400 to-blue-500 text-white">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-blue-100 text-sm">Tracking Since</p>
                <p className="text-xl font-bold mt-1">2026</p>
              </div>
              <TrendingUp className="w-12 h-12 opacity-80" />
            </div>
          </Card>
        </div>

        {/* Children Section */}
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-bold text-gray-800">Your Children</h2>
          <Button
            onClick={() => navigate('/add-child')}
            className="bg-gradient-to-r from-[#FFB4A2] via-[#F7DBA7] to-[#A8D5FF] hover:from-pink-600 hover:to-purple-600 text-white"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Child
          </Button>
        </div>

        {!Array.isArray(children) || children.length === 0 ? (
          <Card className="p-12 text-center bg-white/80 backdrop-blur-sm">
            <div className="w-20 h-20 bg-gradient-to-br from-[#FFB4A2] to-[#F7DBA7] rounded-full flex items-center justify-center mx-auto mb-4">
              <Baby className="w-10 h-10 text-white" />
            </div>
            <h3 className="text-xl font-semibold mb-2 text-gray-800">No Children Added Yet</h3>
            <p className="text-gray-600 mb-6">Add your first child to start tracking foot measurements</p>
            <Button
              onClick={() => navigate('/add-child')}
              className="bg-gradient-to-r from-[#FFB4A2] via-[#F7DBA7] to-[#A8D5FF] hover:from-pink-600 hover:to-purple-600 text-white"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Your First Child
            </Button>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {children.map((child) => {
              // Handle both camelCase and snake_case from API
              const dob = child.dateOfBirth || child.date_of_birth;
              const ageMonths = dob ? calculateAge(dob) : 0;
              const latestMeasurement = getLatestMeasurement(child.id);
              const measurementCount = getChildMeasurements(child.id).length;

              return (
                <Card key={child.id} className="p-6 hover:shadow-lg transition-all duration-300 hover:-translate-y-1 bg-white/80 backdrop-blur-sm cursor-pointer"
                  onClick={() => navigate(`/child/${child.id}`)}>
                  <div className="flex items-start gap-4">
                    <Avatar className="w-16 h-16" style={{ backgroundColor: child.avatarColor }}>
                      <AvatarFallback className="text-white text-xl font-bold" style={{ backgroundColor: child.avatarColor }}>
                        {child.name[0]}
                      </AvatarFallback>
                    </Avatar>
                    <div className="flex-1">
                      <h3 className="text-xl font-semibold text-gray-800">{child.name}</h3>
                      <p className="text-sm text-gray-600">{ageMonths > 0 ? `${ageMonths} months old` : 'Age not set'}</p>
                      <p className="text-xs text-gray-500 mt-1">{measurementCount} measurement{measurementCount !== 1 ? 's' : ''}</p>
                    </div>
                  </div>
                  
                  {latestMeasurement && (
                    <div className="mt-4 pt-4 border-t">
                      <p className="text-xs text-gray-500 mb-2">Latest measurement</p>
                      <div className="grid grid-cols-3 gap-2">
                        <div className="bg-[#FFF5F0] rounded p-2">
                          <p className="text-xs text-gray-600">US Size</p>
                          <p className="font-semibold text-[#FFB4A2]">{latestMeasurement.usSize || latestMeasurement.us_size}</p>
                        </div>
                        <div className="bg-[#F0F8FF] rounded p-2">
                          <p className="text-xs text-gray-600">EU Size</p>
                          <p className="font-semibold text-[#A8D5FF]">{latestMeasurement.euSize || latestMeasurement.eu_size}</p>
                        </div>
                        <div className="bg-blue-50 rounded p-2">
                          <p className="text-xs text-gray-600">UK Size</p>
                          <p className="font-semibold text-[#9ADFB4]">{latestMeasurement.ukSize || latestMeasurement.uk_size}</p>
                        </div>
                      </div>
                    </div>
                  )}

                  <div className="mt-4 flex gap-2">
                    <Button
                      size="sm"
                      className="flex-1 bg-gradient-to-r from-[#FFB4A2] via-[#F7DBA7] to-[#A8D5FF] hover:from-pink-600 hover:to-purple-600 text-white"
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(`/measure/${child.id}`);
                      }}
                    >
                      <Ruler className="w-4 h-4 mr-2" />
                      Measure
                    </Button>
                  </div>
                </Card>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default Dashboard;